// 「접수」 화면 보철 이력 — 이 치과가 덴클로 보낸 의뢰서(workOrders)에서 찾는다 (2026-10-03)
// 쿼리는 OrderList.js 보낸 의뢰서 쿼리(:231-235)와 같은 축 — where('fromUserId','==',치과 uid) 필수(전체 스캔 금지).
// 기존 rules(workOrders read: fromUserId 본인 또는 hasCompanyAccess — firestore.rules:374-380)로 대표·직원 모두 읽힌다. 새 rules 불필요.
//  - 이름 검색: fromUserId == 치과 && patientName == 이름(완전 일치, 등호 2개 → 단일 필드 인덱스 병합, 복합 인덱스 불필요)
//  - 연결된 의뢰서: 접수에 기록된 orderIds 로 문서 직접 조회(linkOrderToReception 서버가 기록)
// 환자명은 콘솔에 남기지 않는다.
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

const toDate = (t) => {
    if (!t) return null;
    if (typeof t.toDate === 'function') return t.toDate();
    if (t instanceof Date) return t;
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
};
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => (d ? `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}` : '-');
const md = (d) => (d ? `${pad(d.getMonth() + 1)}.${pad(d.getDate())}` : '');

// 의뢰서 1건 → { id, date:'2026.09.18', text:'46 임플란트 크라운 · 지르코니아', tail:'완료' | '납기 09.25' }
export function summarizeOrder(o) {
    const items = (o.items || []).filter(i => i && i.toothNumber != null && i.toothNumber !== '');
    const groups = new Map();
    items.forEach(i => {
        const key = `${i.prosthesis || ''}|${i.material || ''}`;
        if (!groups.has(key)) groups.set(key, { teeth: [], prosthesis: i.prosthesis || '', material: i.material || '' });
        groups.get(key).teeth.push(i.toothNumber);
    });
    let text = Array.from(groups.values()).map(g =>
        [g.teeth.join(','), g.prosthesis].filter(Boolean).join(' ') + (g.material ? ` · ${g.material}` : '')).join(' / ');
    if (!text) text = o.prosthesisSummary || '보철물';
    const date = toDate(o.orderDate) || toDate(o.createdAt);
    let tail = '';
    if (o.status === 'completed') tail = '완료';
    else {
        const due = toDate(o.dueDate);
        if (due) tail = `납기 ${md(due)}`;
    }
    return { id: o.id, date: ymd(date), sortKey: date ? date.getTime() : 0, text, tail };
}

const visible = (o) => o.hiddenForSender !== true && o.source !== 'agent_auto' && o.type !== 'message';

// 이름 완전 일치 — 최신순 최대 20건
export async function fetchOrdersByPatientName(clinicId, name) {
    const nm = String(name || '').normalize('NFC').trim();
    if (!clinicId || !nm) return [];
    const snap = await getDocs(query(
        collection(db, 'workOrders'),
        where('fromUserId', '==', clinicId),
        where('patientName', '==', nm),
        limit(50),
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(visible)
        .map(summarizeOrder).sort((a, b) => b.sortKey - a.sortKey).slice(0, 20);
}

// 연결된 의뢰서 — id 목록(최대 20) 직접 조회. 못 읽은 건(삭제·권한)은 missing 으로 센다(조용히 버리지 않음)
export async function fetchOrdersByIds(ids) {
    const list = Array.from(new Set((ids || []).map(String).filter(Boolean))).slice(0, 20);
    let missing = 0;
    const rows = await Promise.all(list.map(async (id) => {
        try {
            const s = await getDoc(doc(db, 'workOrders', id));
            if (!s.exists()) { missing++; return null; }
            return summarizeOrder({ id: s.id, ...s.data() });
        } catch (e) {
            console.error('[접수] 연결 의뢰서 조회 실패:', id, e?.code || e?.message || e);
            missing++;
            return null;
        }
    }));
    return { rows: rows.filter(Boolean).sort((a, b) => b.sortKey - a.sortKey), missing };
}
